import React, { useEffect, useState } from 'react'
import { socket } from '../socket'
import { TbPlugConnectedX } from 'react-icons/tb'
import { PiPlugsConnected } from 'react-icons/pi'
import colors from '../config/colors.json'
import { useSocketState } from '../helpers/useSocket'
import { FaExternalLinkAlt } from 'react-icons/fa'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import PlayerPicker from '../components/PlayerPicker'
import { createContext } from 'react'
import { clearToken, isAuthEnabled, getToken } from '../helpers/auth'

export const ThemeContext = createContext()

export default function Dashboard() {
	const navigate = useNavigate()

	const [theme, setTheme] = useState(() => {
		return (
			localStorage.getItem('theme') ||
			(window.matchMedia('(prefers-color-scheme: dark)').matches
				? 'dark'
				: 'light')
		)
	})

	const [isConnected, setIsConnected] = useState(socket.connected)
	const [config, setConfig] = useState({})

	const [tournament] = useSocketState('Tournament')
	const [match] = useSocketState('Match')

	function refreshGames() {
		socket.emit('refreshGames')
	}

	function modifyLeftPlayer(data) {
		socket.emit('modifyLeftPlayer', data)
	}

	function modifyRightPlayer(data) {
		socket.emit('modifyRightPlayer', data)
	}

	function toggleFlipPlayersIngame() {
		socket.emit('modifyMatch', {
			flipPlayersIngame: !match.flipPlayersIngame,
		})
	}

	const toggleTheme = () => {
		setTheme((curr) => (curr === 'light' ? 'dark' : 'light'))
	}

	function handleLogout() {
		clearToken()
		navigate('/login')
	}

	useEffect(() => {
		function onConnect() {
			setIsConnected(true)
		}

		function onDisconnect() {
			setIsConnected(false)
		}

		function onUpdateConfig(value) {
			console.log(`Received updateConfig`, value)
			setConfig((oldState) => ({ ...oldState, ...value }))
		}

		socket.on('connect', onConnect)
		socket.on('disconnect', onDisconnect)
		socket.on('updateConfig', onUpdateConfig)

		return () => {
			socket.off('connect', onConnect)
			socket.off('disconnect', onDisconnect)
			socket.off('updateConfig', onUpdateConfig)
		}
	}, [])

	useEffect(() => {
		localStorage.setItem('theme', theme)
	}, [theme])

	function handleResetMatch() {
		if (window.confirm('Reset ALL match settings?')) {
			socket.emit('resetMatch')
			socket.emit('setGameCivDraftId', null)
			socket.emit('setCivDraftId', null)
			socket.emit('setMapDraftId', null)
			console.log('resetting match')
		} else {
			console.log('not resetting')
		}
	}

	return (
		<ThemeContext.Provider value={{ theme, toggleTheme }}>
			<div className={`pl-4 dashboard ${theme}`}>
				<div className="absolute right-0 flex flex-row">
					<div
						className="inline-flex items-center cursor-pointer flex-col gap-1"
						onClick={toggleTheme}
					>
						{theme === 'light' ? '🌞' : '🌚'}
						<input
							type="checkbox"
							checked={theme === 'dark'}
							className="sr-only peer"
							onChange={toggleTheme}
						/>
						<div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
						{isAuthEnabled() && (
							<button
								className="text-xs px-2 py-0.5 bg-gray-500 hover:bg-gray-600 text-white rounded"
								onClick={handleLogout}
							>
								Logout
							</button>
						)}
					</div>

					<div className="flex flex-col gap-4 pr-4">
						<div>
							Connected:{' '}
							{isConnected ? (
								<PiPlugsConnected size={30} />
							) : (
								<TbPlugConnectedX size={30} />
							)}
						</div>
						<div className="-mt-4">ID: {config.tournament_id}</div>
					</div>
				</div>

				<div className="mb-2">
					{window.__RUNTIME_CONFIG__.REACT_APP_ENABLE_DRAFT ===
					'true' ? (
						<Link className="mx-1" to="/Dashboard/Draft">
							<button className="rounded-t-none">
								Draft/Match
							</button>
						</Link>
					) : (
						<></>
					)}

					<Link className="mx-1" to="/Dashboard/Casters">
						<button className="rounded-t-none">Casters</button>
					</Link>

					{window.__RUNTIME_CONFIG__.REACT_APP_ENABLE_LIVEGAME ===
					'true' ? (
						<Link className="mx-1" to="/Dashboard/LiveGame">
							<button className="rounded-t-none">LiveGame</button>
						</Link>
					) : (
						<></>
					)}

					{window.__RUNTIME_CONFIG__.REACT_APP_ENABLE_POSTGAME ===
					'true' ? (
						<Link className="mx-1" to="/Dashboard/PostGame">
							<button className="rounded-t-none">PostGame</button>
						</Link>
					) : (
						<></>
					)}

					{window.__RUNTIME_CONFIG__.REACT_APP_ENABLE_BRACKET ===
					'true' ? (
						<Link className="mx-1" to="/Dashboard/Bracket">
							<button className="rounded-t-none">Bracket</button>
						</Link>
					) : (
						<></>
					)}

					{window.__RUNTIME_CONFIG__
						.REACT_APP_ENABLE_CONFIG_EDITOR === 'true' ? (
						<Link className="mx-1" to="/Dashboard/ConfigEditor">
							<button className="rounded-t-none">
								Config Editor
							</button>
						</Link>
					) : (
						<></>
					)}

					<Link className="mx-1" to="/Dashboard/Settings">
						<button className="rounded-t-none">Settings</button>
					</Link>

					{(() => {
						const tq =
							isAuthEnabled() && getToken()
								? `?token=${getToken()}`
								: ''
						return (
							<>
								{window.__RUNTIME_CONFIG__
									.REACT_APP_ENABLE_DRAFT === 'true' ? (
									<Link
										className="mx-1"
										to={`/DraftDisplay${tq}`}
										target="_blank"
									>
										<button className="bg-teal-700 hover:bg-teal-800 rounded-t-none ml-8">
											Draft Viewer
											<FaExternalLinkAlt />
										</button>
									</Link>
								) : (
									<></>
								)}

								<Link
									className="mx-1"
									to={`/LiveGame${tq}`}
									target="_blank"
								>
									<button className="bg-teal-700 hover:bg-teal-800 rounded-t-none">
										Live Game Viewer
										<FaExternalLinkAlt />
									</button>
								</Link>

								{window.__RUNTIME_CONFIG__
									.REACT_APP_ENABLE_POSTGAME === 'true' ? (
									<Link
										className="mx-1"
										to={`/${tq}`}
										target="_blank"
									>
										<button className="bg-teal-700 hover:bg-teal-800 rounded-t-none">
											Post Game Viewer
											<FaExternalLinkAlt />
										</button>
									</Link>
								) : (
									<></>
								)}
							</>
						)
					})()}
				</div>
				{match && (
					<div className="flex flex-row">
						<div className="grid grid-cols-3 gap-y-2 w-[480px]">
							<div className="my-4">
								<label>
									Left Player{' '}
									<span className="text-sm text-gray-500 ml-5"></span>
								</label>
								<PlayerPicker
									players={tournament?.config?.players}
									value={match?.leftPlayer.name}
									onChange={(name) =>
										modifyLeftPlayer({ name })
									}
									countryValue={match?.leftPlayer.country?.toUpperCase()}
									countryOnChange={(country) =>
										modifyLeftPlayer({ country })
									}
								/>
							</div>
							<div></div>
							<div className="my-4">
								<label>Right Player</label>
								<PlayerPicker
									players={tournament?.config?.players}
									value={match?.rightPlayer.name}
									onChange={(name) =>
										modifyRightPlayer({ name })
									}
									countryValue={match?.rightPlayer.country?.toUpperCase()}
									countryOnChange={(country) =>
										modifyRightPlayer({ country })
									}
								/>
							</div>
							<div className="w-24 mx-auto">
								<label>Left Score</label>
								<input
									type="number"
									value={match?.leftPlayer.score}
									onChange={(e) =>
										modifyLeftPlayer({
											score: Number(e.target.value),
										})
									}
									id="leftWins"
									min="0"
									name="leftWins"
								/>
							</div>
							<div className="flex flex-col justify-center items-center">
								<span className="ms-3 text-sm font-medium text-gray-900">
									Swap Ingame
								</span>
								<label className="inline-flex items-center cursor-pointer">
									<input
										type="checkbox"
										checked={match?.flipPlayersIngame}
										className="sr-only peer"
										onChange={toggleFlipPlayersIngame}
									/>
									<div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
								</label>
							</div>
							<div className="w-24 mx-auto">
								<label>Right Score</label>
								<input
									type="number"
									value={match?.rightPlayer.score}
									onChange={(e) =>
										modifyRightPlayer({
											score: Number(e.target.value),
										})
									}
									id="rightWins"
									min="0"
									name="rightWins"
								/>
							</div>
							<div className="w-24 mx-auto">
								<label>Left Colour</label>
								<select
									value={match?.leftPlayer.color}
									onChange={(e) =>
										modifyLeftPlayer({
											color: String(e.target.value),
										})
									}
									id="leftColor"
									name="leftColor"
									defaultValue={'blue'}
								>
									{Object.entries(colors).map(
										([colorname, colorValue]) => (
											<option
												value={`${colorname}`}
											>{`${colorname}`}</option>
										),
									)}
								</select>
							</div>
							<div></div>
							<div className="w-24 mx-auto">
								<label>Right Color</label>
								<select
									value={match?.rightPlayer.color}
									onChange={(e) =>
										modifyRightPlayer({
											color: String(e.target.value),
										})
									}
									id="rightColor"
									name="rightColor"
									defaultValue={'red'}
								>
									{Object.entries(colors).map(
										([colorname, colorValue]) => (
											<option
												value={`${colorname}`}
											>{`${colorname}`}</option>
										),
									)}
								</select>
							</div>
						</div>
						<div>
							<button
								className="h-8 my-auto mr-4 ml-[120px] bg-red-600 hover:bg-red-800"
								onClick={handleResetMatch}
							>
								Reset Match
							</button>
						</div>
					</div>
				)}
				<hr className="h-px my-2 bg-gray-200 border-0 dark:bg-gray-700"></hr>
				<Outlet context={{ socket, refreshGames, config }} />
			</div>
		</ThemeContext.Provider>
	)
}
